
import { Component, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import ExchangeRatesInjectorMixin from "@/mixins/exchangeRatesInjectorMixin";

// Providers
import { ICurrency } from "@/models/constants";
import { IExchangeRateMatrix } from "@/models/exchangeRates";

@Component
export default class ManualExchangeRate extends Mixins(
  ExchangeRatesInjectorMixin
) {
  // Refs

  public $refs!: {
    realFauxInput: HTMLFormElement;
    realRateInput: HTMLFormElement;
  };

  // Props

  @Prop({ required: true })
  public value!: IExchangeRateMatrix;

  @Prop({ required: true })
  public baseCurrency!: ICurrency;

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Data

  public isSelected = false;

  public oldValue;

  public created() {
    this.oldValue = this.exchangeRate.value || "1.000000";
  }

  // Getters

  public get exchangeRate() {
    return this.value;
  }

  // Setters

  public set exchangeRate(value) {
    this.$emit("input", value);
  }

  // Computed

  public get isBrandRate() {
    return !_.isNull(this.exchangeRate.value);
  }

  public get isOrgInheritedRate() {
    return (
      _.isNull(this.exchangeRate.value) && !_.isNull(this.exchangeRate.orgValue)
    );
  }

  public get isOrgMode() {
    return this.$exchangeRatesData.isOrgMode;
  }

  // Methods

  public formatDecimal(value) {
    return parseFloat(value).toFixed(6);
  }

  public onBlur(onEnter = false) {
    setTimeout(() => {
      this.isSelected = false;
      if (_.isNull(this.exchangeRate.value)) return;

      if (this.exchangeRate.value === "") {
        this.onCheckboxChange(true);
      } else {
        this.exchangeRate.value =
          this.isOrgMode &&
          this.exchangeRate.orgValue === this.exchangeRate.value
            ? null
            : this.formatDecimal(this.exchangeRate.value);
      }

      if (onEnter) {
        this.$emit("submit");
      }
    }, 150);
  }

  // Render Function

  public render(h: CreateElement) {
    return h("div", { class: "u-manual-exchange-rate" }, [
      h(
        "strong",
        { staticClass: "is-family-monospace" },
        this.exchangeRate.code
      ),
      h(
        "currency-flag",
        { props: { currencyCode: this.exchangeRate.code } },
        []
      ),
      h("b-checkbox", {
        props: {
          disabled: this.isDisabled,
          value: this.exchangeRate.isDynamic
        },
        on: {
          input: this.onCheckboxChange
        },
        class: "u-manual-exchange-rate-switcher"
      }),
      this.exchangeRate.isDynamic
        ? h("div", [this.createFauxInput(h)])
        : h("b-field", { class: "is-marginless" }, [this.createRealInput(h)])
    ]);
  }

  public onCheckboxChange(isDynamic) {
    if (isDynamic) {
      if (!this.isOrgMode && this.exchangeRate.value !== "")
        this.oldValue = this.formatDecimal(this.exchangeRate.value);

      this.exchangeRate.value = null;
    } else {
      this.exchangeRate.value =
        this.exchangeRate.orgValue && this.oldValue === "1.000000"
          ? this.exchangeRate.orgValue
          : this.oldValue;

      this.$nextTick(() => {
        this.$refs.realFauxInput.click();
        this.$nextTick(() => {
          this.$refs.realRateInput.select();
        });
      });
    }

    this.exchangeRate.active = !isDynamic;
    this.exchangeRate.isDynamic = isDynamic;
  }

  public createFauxInput(h: CreateElement) {
    return h(
      "div",
      {
        class: _.compact([
          "u-rate-input",
          "is-expanded",
          "is-clearfix",
          "input",
          "has-cursor-text"
        ]).join(" "),
        attrs: {
          tabindex: `0`
        }
      },
      [this.formatValue(h), this.createInputIcon(h)]
    );
  }

  public formatValue(h: CreateElement) {
    // Org mode
    if (this.isOrgMode) {
      return this.exchangeRate.isDynamic
        ? this.createDynamicRateStyle(h)
        : this.createValueRateStyle(
            h,
            `${this.exchangeRate.value || this.exchangeRate.orgValue}`
          );
    }

    // Brand mode
    return this.isBrandRate
      ? this.createValueRateStyle(h, `${this.exchangeRate.value}`)
      : this.isOrgInheritedRate
      ? this.createInheritedRateStyle(h)
      : this.createDynamicRateStyle(h);
  }

  public createInputIcon(h: CreateElement) {
    // Org mode
    if (this.isOrgMode) return this.createDynamicRateIconStyle(h);

    // Brand mode
    return this.isOrgInheritedRate
      ? this.createInheritedRateIconStyle(h)
      : this.createDynamicRateIconStyle(h);
  }

  public createDynamicRateStyle(h: CreateElement) {
    return h(
      "span",
      { class: "has-text-info has-cursor-default" },
      this.$t("_.dynamic_rate").toString()
    );
  }

  public createValueRateStyle(h: CreateElement, value: string) {
    return h("span", { class: "has-text-grey-light" }, [
      `1${this.baseCurrency.code} = `,
      h("span", { class: "has-text-dark" }, value)
    ]);
  }

  public createInheritedRateStyle(h: CreateElement) {
    return h(
      "span",
      { class: "has-text-grey-light" },
      `1${this.baseCurrency.code} = ${this.exchangeRate.orgValue}`
    );
  }

  public createInheritedRateIconStyle(h: CreateElement) {
    return h(
      "b-tooltip",
      {
        class: "has-icon-right",
        props: {
          label: this.$t("_sentence.inherited_exchange_rate_desc"),
          type: "is-dark",
          position: "is-left",
          size: "is-small",
          multilined: true
        }
      },
      [
        h("b-icon", {
          props: {
            icon: "link",
            size: "is-small",
            type: "is-grey-light"
          }
        })
      ]
    );
  }

  public createDynamicRateIconStyle(h: CreateElement) {
    return h("b-icon", {
      class: "has-icon-right",
      props: {
        icon: "calculator",
        size: "is-small",
        type: "is-info"
      }
    });
  }

  public createRealInput(h: CreateElement) {
    if (this.isSelected) {
      return h(
        "input",
        {
          attrs: {
            type: "number",
            min: 0.000001,
            step: 0.000001,
            value:
              this.isOrgMode && _.isNull(this.exchangeRate.value)
                ? this.exchangeRate.orgValue
                : this.exchangeRate.value
          },
          ref: "realRateInput",
          class: "input u-real-rate-input is-expanded",
          on: {
            blur: () => {
              this.onBlur();
            },
            input: event => {
              this.exchangeRate.value = event.target.value;
            },
            keydown: event => {
              if (event.key === "Enter") {
                event.stopPropagation();
                event.preventDefault();

                this.onBlur(true);
              }
            }
          }
        },
        []
      );
    }

    return h(
      "div",
      {
        class: _.concat(
          [
            "u-real-rate-input",
            "is-expanded",
            "is-clearfix",
            "input",
            "has-cursor-text"
          ],
          this.isDisabled ? ["is-disabled"] : []
        ).join(" "),
        attrs: {
          tabindex: `0`
        },
        ref: "realFauxInput",
        on: {
          click: () => {
            if (this.isDisabled) return;
            this.isSelected = true;
          },
          focus: () => {
            if (this.isDisabled) return;

            this.isSelected = true;
            this.$nextTick(() => {
              if (this.$_.has(this.$refs, "realRateInput")) {
                this.$refs.realRateInput.select();
              }
            });
          }
        }
      },
      [this.formatValue(h)]
    );
  }
}
